import React from 'react';
import { Card } from 'react-bootstrap';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';

// Liver / Biliary → Insights tab, second card (after the Hepatic Steatosis
// Index). Shows how the FIB-4 index was calculated for THIS test: Sterling
// 2006's formula with the test's own age, AST, ALT and platelet count
// substituted in. Client-facing and non-diagnostic, same voice as the HSI and
// viscosity cards: it explains the number, it doesn't interpret it. The
// calculation lives in BloodworkAnalysis.populateFib4PanelMarker; this card
// only re-reads its inputs.

const trim = (n: number): string => String(Math.round(n * 100) / 100);
const fixed2 = (n: number): string => (Math.round(n * 100) / 100).toFixed(2);

const formulaStyle: React.CSSProperties = {
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  fontSize: '14px',
  lineHeight: 1.9,
  color: '#20527f',
  whiteSpace: 'nowrap',
  overflowX: 'auto',
  display: 'block',
};

const noteHeadingStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '13px',
  color: '#20527f',
  margin: '0 0 6px',
};

const noteStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#5b6675',
  lineHeight: 1.6,
  margin: '0 0 10px',
};

const sensitivityTableStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#5b6675',
  borderCollapse: 'collapse',
  margin: '0 0 10px',
};

const sensitivityCellStyle: React.CSSProperties = {
  padding: '3px 14px 3px 0',
  borderBottom: '1px solid #eef1f4',
  whiteSpace: 'nowrap',
};

// How far each input moves the index across a typical span. Age and AST
// multiply it directly; platelets divide it; ALT enters as a square root so it
// pulls less. Kept as data so the card and the Notes digest can't drift apart.
const SENSITIVITY_ROWS = [
  { input: 'Age', span: '40 → 65 years', effect: 'raises the index by about 60 percent' },
  { input: 'Platelets', span: '250 → 150', effect: 'raises the index by about 65 percent' },
  { input: 'AST', span: '20 → 40 U/L', effect: 'doubles the index' },
  { input: 'ALT', span: '20 → 40 U/L', effect: 'lowers the index by about 30 percent' },
];

// The rule-out rises at 65 (McPherson 2017, EASL 2024); the stored range rows
// carry the same split (optimal max 1.3 to 64, 2.0 from 65).
export const FIB4_AGE_SPLIT = 65;

const READING_INTRO = (age: number): string =>
  `A result under ${age >= FIB4_AGE_SPLIT ? '2.0' : '1.3'} makes advanced scarring unlikely` +
  (age >= FIB4_AGE_SPLIT ? ' (the cutoff is 1.3 before age 65)' : ' (2.0 from age 65)') +
  ', over 2.67 makes it likely enough that a liver stiffness scan is the next step, ' +
  'and the band between is undecided. The index speaks only to scarring, not to ' +
  'fat or inflammation, and the reference bar above shows the same three bands.';

const READING_OUTRO =
  'Two levers can move this index with no change in the liver: a platelet count ' +
  'lowered for another reason, and an AST raised from muscle, exertion or a ' +
  'sample with red cell breakage. Age also multiplies it, so the same enzymes ' +
  'read higher every year. Below age 35 a low result does not rule scarring ' +
  'out. The index is best read alongside the platelet, AST and ALT bars, and ' +
  'against its own value on earlier tests.';

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
};

export type Fib4Computation = {
  age: number;
  ast: number;
  alt: number;
  platelets: number;
  numerator: number;
  sqrtAlt: number;
  denominator: number;
  result: number;
};

export class Fib4Insights extends React.Component<Props, {}> {
  static shouldRender(bloodworkAnalysis: BloodworkAnalysis): boolean {
    const v = bloodworkAnalysis && bloodworkAnalysis.fib4;
    return !!(v && v.hasValue());
  }

  static compute(bloodworkAnalysis: BloodworkAnalysis): Fib4Computation | null {
    if (!Fib4Insights.shouldRender(bloodworkAnalysis)) {
      return null;
    }
    const bw = bloodworkAnalysis.bloodwork;
    const age = bw.client.ageAsOf(bw.collectionDate);
    const ast = bloodworkAnalysis.ast.value;
    const alt = bloodworkAnalysis.alt.value;
    const platelets = bloodworkAnalysis.platelets.value;
    const sqrtAlt = Math.sqrt(alt);
    return {
      age,
      ast,
      alt,
      platelets,
      numerator: age * ast,
      sqrtAlt,
      denominator: platelets * sqrtAlt,
      result: bloodworkAnalysis.fib4.value,
    };
  }

  // Plain-text version for the Notes "gather from tabs" digest and the AI export.
  static digestGroup(
    bloodworkAnalysis: BloodworkAnalysis,
  ): { title: string; items: string[] } | null {
    const c = Fib4Insights.compute(bloodworkAnalysis);
    if (!c) {
      return null;
    }
    return {
      title: 'Fibrosis-4 Index (FIB-4)',
      items: [
        'The FIB-4 index is not measured directly. It is calculated from age at ' +
          "the time of the test and this test's AST, ALT and platelet count " +
          '(Sterling and colleagues, 2006): age × AST ÷ (platelets × √ALT)',
        `Using this test's values (age = ${c.age}, AST = ${trim(c.ast)} U/L, ALT = ${trim(c.alt)} U/L, platelets = ${trim(c.platelets)}):`,
        `= (${c.age} × ${trim(c.ast)}) ÷ (${trim(c.platelets)} × √${trim(c.alt)})`,
        `= ${fixed2(c.numerator)} ÷ (${trim(c.platelets)} × ${fixed2(c.sqrtAlt)})`,
        `= ${fixed2(c.numerator)} ÷ ${fixed2(c.denominator)}`,
        `= ${fixed2(c.result)}`,
        'Reading the result: ' + READING_INTRO(c.age),
        ...SENSITIVITY_ROWS.map(r => `${r.input} (${r.span}) ${r.effect}`),
        READING_OUTRO,
      ],
    };
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    const c = Fib4Insights.compute(bloodworkAnalysis);
    if (!c) {
      return null;
    }

    return (
      <Card style={{ marginBottom: 12 }}>
        <Card.Header style={{ fontWeight: 'bold' }}>Fibrosis-4 Index (FIB-4)</Card.Header>
        <Card.Body>
          <p style={{ marginBottom: 12 }}>
            The FIB-4 index is not measured directly.
            <br />
            It is calculated from <strong>age</strong> at the time of the test and
            this test's <strong>AST</strong>, <strong>ALT</strong> and{' '}
            <strong>platelet count</strong>, using the formula published by
            Sterling and colleagues in 2006:
          </p>
          <code style={formulaStyle}>age × AST ÷ (platelets × √ALT)</code>
          <p style={{ margin: '10px 0 6px', color: '#5b6675' }}>
            Using this test's values (age = {c.age}, AST = {trim(c.ast)} U/L, ALT ={' '}
            {trim(c.alt)} U/L, platelets = {trim(c.platelets)}):
          </p>
          <code style={formulaStyle}>
            = ({c.age} × {trim(c.ast)}) ÷ ({trim(c.platelets)} × √{trim(c.alt)})
            <br />
            = {fixed2(c.numerator)} ÷ ({trim(c.platelets)} × {fixed2(c.sqrtAlt)})
            <br />
            = {fixed2(c.numerator)} ÷ {fixed2(c.denominator)}
            <br />
            <strong>= {fixed2(c.result)}</strong>
          </code>

          <hr style={{ margin: '16px 0 12px', borderColor: '#eef1f4' }} />

          <p style={noteHeadingStyle}>Reading the result</p>
          <p style={noteStyle}>{READING_INTRO(c.age)}</p>
          <table style={sensitivityTableStyle}>
            <tbody>
              {SENSITIVITY_ROWS.map(r => (
                <tr key={r.input}>
                  <td style={sensitivityCellStyle}>{r.input}</td>
                  <td style={sensitivityCellStyle}>{r.span}</td>
                  <td style={sensitivityCellStyle}>{r.effect}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ ...noteStyle, marginBottom: 0 }}>{READING_OUTRO}</p>
        </Card.Body>
      </Card>
    );
  }
}
